import { useI18n } from "@/i18n";

export function PageHeader({ title, subtitle, actions, testid }) {
  // Legacy — kept for any remaining usage. Prefer PageShell.
  return (
    <div className="flex items-end justify-between gap-6 mb-10" data-testid={testid}>
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-3">Dashboard</div>
        <h1 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 dark:text-white">{title}</h1>
        {subtitle && <p className="text-sm text-slate-600 dark:text-slate-400 mt-2 max-w-2xl">{subtitle}</p>}
      </div>
      {actions && <div className="flex gap-2 shrink-0">{actions}</div>}
    </div>
  );
}

export function PageContainer({ children }) {
  return <div className="p-6 md:p-8 lg:p-12 max-w-[1600px] mx-auto">{children}</div>;
}

export function KpiCard({ label, value, hint, accent = "slate", testid }) {
  const accents = {
    slate: "text-slate-900 dark:text-white",
    orange: "text-orange-600 dark:text-orange-400",
    amber: "text-amber-600 dark:text-amber-400",
    red: "text-red-600 dark:text-red-400",
    green: "text-green-600 dark:text-green-400",
    blue: "text-blue-600 dark:text-blue-400",
  };
  return (
    <div
      data-testid={testid}
      className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md p-5 transition-all hover:border-slate-300 dark:hover:border-slate-700 hover:shadow-sm hover:-translate-y-[1px]"
    >
      <div className="text-xs uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400 font-semibold">{label}</div>
      <div className={`mt-3 text-3xl font-semibold tracking-tight font-mono ${accents[accent]}`}>{value}</div>
      {hint && <div className="text-xs text-slate-500 dark:text-slate-400 mt-2">{hint}</div>}
    </div>
  );
}

const STATUS_I18N = {
  paid: { es: "Pagada", en: "Paid" },
  pending: { es: "Pendiente", en: "Pending" },
  overdue: { es: "Vencida", en: "Overdue" },
  disconnected: { es: "Desconectada", en: "Disconnected" },
  refunded: { es: "Reembolsada", en: "Refunded" },
  partially_refunded: { es: "Reemb. parcial", en: "Partially refunded" },
  canceled: { es: "Cancelada", en: "Canceled" },
  cancelled: { es: "Cancelada", en: "Cancelled" },
  active: { es: "Activo", en: "Active" },
  inactive: { es: "Inactivo", en: "Inactive" },
  critical: { es: "Mora crítica", en: "Critical" },
  transferred: { es: "Trasladado", en: "Transferred" },
};

export function StatusBadge({ status, size = "sm" }) {
  const { lang } = useI18n();
  const map = {
    paid: "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800",
    pending: "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800",
    overdue: "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:border-rose-800",
    disconnected: "bg-red-100 text-red-800 border-red-300 dark:bg-red-950/40 dark:text-red-300 dark:border-red-800",
    refunded: "bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700",
    partially_refunded: "bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700",
    canceled: "bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700",
    cancelled: "bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700",
    active: "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800",
    inactive: "bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700",
    critical: "bg-red-100 text-red-800 border-red-300 dark:bg-red-950/40 dark:text-red-300 dark:border-red-800",
    transferred: "bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700",
  };
  const label = STATUS_I18N[status]?.[lang] || status;
  const cls = map[status] || "bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700";
  const sizeCls = size === "xs" ? "px-1.5 py-0.5 text-[10px]" : "px-2.5 py-0.5 text-xs";
  return (
    <span className={`inline-flex items-center rounded-md font-semibold border ${sizeCls} ${cls}`}>
      {label}
    </span>
  );
}

export function fmtMoney(n) {
  if (n == null) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);
}

export function fmtDate(d) {
  // Numeric MM/DD/YYYY — corporate, unambiguous, dense.
  if (!d) return "—";
  const date = typeof d === "string" ? new Date(d) : d;
  if (isNaN(date)) return d;
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  const yyyy = date.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
}

/**
 * EmptyState — used inside tables/cards to communicate "nothing here yet".
 * Renders an outline icon, title, descriptive hint, and optional action button.
 */
export function EmptyState({ icon: Icon, title, message, action, compact = false }) {
  return (
    <div className={`text-center ${compact ? "py-10" : "py-16"}`} data-testid="empty-state">
      {Icon && (
        <div className="mx-auto w-12 h-12 rounded-full bg-slate-50 border border-slate-200 flex items-center justify-center mb-4">
          <Icon size={22} weight="duotone" className="text-slate-400" />
        </div>
      )}
      <div className="text-sm font-semibold text-slate-900">{title}</div>
      {message && <div className="text-xs text-slate-500 mt-1 max-w-sm mx-auto leading-relaxed">{message}</div>}
      {action && <div className="mt-5 flex justify-center">{action}</div>}
    </div>
  );
}

/**
 * Skeleton — animated gray block for loading states.
 */
export function Skeleton({ className = "" }) {
  return <div className={`bg-slate-100 rounded animate-pulse ${className}`} />;
}

/**
 * SkeletonRow — skeleton row matching a table layout.
 */
export function SkeletonRow({ cols = 5, height = "h-4" }) {
  return (
    <tr className="border-b border-slate-50">
      {Array.from({ length: cols }).map((_, i) => (
        <td key={i} className="py-3 px-5">
          <Skeleton className={`${height} ${i === 0 ? "w-3/5" : "w-4/5"}`} />
        </td>
      ))}
    </tr>
  );
}

/**
 * Corporate input className helper — applies consistent height + borders
 * to <Input /> components inside dialogs.
 */
export const corporateInput =
  "h-11 border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900";
